/* eslint-disable no-await-in-loop */
/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useUpdateEffect, useSize, useDebounceFn } from '@umijs/hooks';
import classnames from 'classnames/bind';

import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Keyboard, Swiper as SwiperClass } from 'swiper/core';
import MeetingModal from '@/models/meeting';
import { Icon } from '@/components';
import ViewBox, { viewBoxType } from '../ViewBox';

import Style from './index.less';
import 'swiper/swiper.less';

const $c = classnames.bind(Style);

SwiperCore.use([Keyboard]);

interface ViewPanelProps {
  meeting?: MeetingModal;
}

const LIMIT = 4;
const ViewPanel: React.FC<ViewPanelProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
  };
})(
  observer((props: ViewPanelProps) => {
    const { meeting } = props;
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const swiperRef = React.useRef<SwiperClass>();
    const [{ width: panelWidth, height: panelHeight }, viewPanelWrapRef] =
      useSize<HTMLDivElement>();
    const [{ width: sWidth, height: sHeight }, swiperElement] = useSize<HTMLDivElement>();
    const [gridSize, setGridSize] = useState({});
    const [gridType, setGridType] = useState('grid-9');

    const { run: gridChange } = useDebounceFn(
      () => {
        if (panelWidth && panelHeight) {
          if (panelWidth < panelHeight) {
            setGridSize({
              width: panelWidth,
              height: panelWidth,
            });
          } else {
            setGridSize({
              width: panelWidth,
              height: panelHeight,
            });
          }
        }
        if (Number(meeting?.sortYxuids.length) < 4) {
          setGridType('grid-4');
        } else {
          setGridType('grid-more');
        }
      },
      [panelWidth, panelHeight, meeting?.sortYxuids.length],
      100
    );

    const onSwiper = React.useCallback(
      (swiper) => {
        swiperRef.current = swiper;
        swiperElement.current = swiper.el;
      },
      [swiperElement]
    );

    useEffect(() => {
      gridChange();
    }, [sWidth, sHeight, meeting?.sortYxuids.length]);
    const pageSize = React.useMemo(() => {
      return meeting ? Math.ceil(meeting.sortYxuids.length / LIMIT) : 10;
    }, [meeting, meeting?.sortYxuids]);

    const keyboardSlide = React.useCallback(async (pageIndex: number) => {
      setCurrentPage(pageIndex + 1);
    }, []);

    const swiperWidth = React.useMemo(() => {
      if (window.isMobile) return panelWidth || 0;
      return panelWidth ? panelWidth - 24 : 0;
    }, [panelWidth]);

    // 当参会人变化时，计算当前页是否还存在，存在则不变，不存在则 currentPage - 1
    useUpdateEffect(() => {
      if (currentPage > pageSize) {
        setCurrentPage(currentPage === 1 ? 1 : currentPage - 1);
      }
    }, [pageSize, currentPage]);

    return (
      <div className={$c('view-panel-wrap', 'grid')} ref={viewPanelWrapRef}>
        <div className={Style['wrap-top']}>
          <div className={$c('speaker')}>
            <Icon
              className={$c('speaking-icon', { 'no-speaker': !meeting?.speakerName })}
              name="icon-speaking"
            />
            {meeting?.speakerName && (
              <span className={$c('speaker-name')}>{meeting?.speakerName}</span>
            )}
          </div>
          <div
            className={classnames({ [Style['hide-o']]: pageSize < 2, [Style['swiper-pages']]: 1 })}
          >
            {pageSize <= 5 && (
              <div className={Style['pages-list']}>
                {new Array(pageSize).fill(1).map((n, index) => (
                  <i
                    key={index}
                    className={classnames({ [Style.active]: currentPage - 1 === index })}
                  />
                ))}
              </div>
            )}
            {pageSize > 5 && (
              <div className={Style['pages-number']}>
                {currentPage}/{pageSize}
              </div>
            )}
          </div>
        </div>
        <Swiper
          className="popo-swiper"
          spaceBetween={1000}
          slidesPerView={1}
          allowTouchMove
          onSwiper={onSwiper}
          onSlideChange={({ realIndex }) => keyboardSlide(realIndex)}
          width={swiperWidth}
        >
          {new Array(pageSize).fill(1).map((num, index) => {
            const members =
              (meeting && meeting.sortYxuids.slice(index * LIMIT, LIMIT * (index + 1))) || [];
            return (
              <SwiperSlide key={index}>
                <div
                  className={classnames(Style['view-panel'], Style[gridType])}
                  style={{ ...gridSize }}
                >
                  {members.map((yxuid: string) => {
                    return (
                      <div key={yxuid} className={Style['grid-box']}>
                        <ViewBox key={yxuid} yxuid={yxuid} type={viewBoxType.video} />
                      </div>
                    );
                  })}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );
  })
);
export default ViewPanel;
